// FieldEditorCard.tsx
import { Card } from '@components/card/card';
import type { Align, FieldKey, LabelLayoutConfig } from './labelLayout';

type Props = {
  title: string;
  fieldKey: FieldKey;
  isBarcode?: boolean;
  layout: LabelLayoutConfig;
  setLayoutField: <K extends FieldKey>(key: K, patch: Partial<LabelLayoutConfig['fields'][K]>) => void;
};

export default function FieldEditorCard({ title, fieldKey, isBarcode, layout, setLayoutField }: Props) {
  const f: any = layout.fields[fieldKey];

  return (
    <Card className='mb-2'>
      <div className='card-body p-2'>
        <div className='d-flex align-items-center justify-content-between mb-2'>
          <div className='small fw-semibold'>
            <i className='bi bi-sliders me-1'></i>
            {title}
          </div>

          <div className='form-check form-switch m-0'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={!f.hidden}
              onChange={e => setLayoutField(fieldKey as any, { hidden: !e.target.checked } as any)}
            />
            <label className='small text-muted ms-1'>إظهار</label>
          </div>
        </div>

        <div className='row g-2'>
          {[
            { k: 'xMm', label: 'X' },
            { k: 'yMm', label: 'Y' },
            { k: 'wMm', label: 'W' },
            { k: 'hMm', label: 'H' }
          ].map(it => (
            <div key={it.k} className='col-3'>
              <div className='input-group input-group-sm'>
                <span className='input-group-text'>{it.label}</span>
                <input
                  type='number'
                  step='0.1'
                  className='form-control'
                  value={Number(f[it.k] ?? 0)}
                  onChange={e => setLayoutField(fieldKey as any, { [it.k]: Number(e.target.value || 0) } as any)}
                />
              </div>
            </div>
          ))}

          {!isBarcode && (
            <>
              <div className='col-3'>
                <div className='input-group input-group-sm'>
                  <span className='input-group-text'>Pt</span>
                  <input
                    type='number'
                    step='0.5'
                    className='form-control'
                    value={Number(f.fontPt ?? 10)}
                    onChange={e => setLayoutField(fieldKey as any, { fontPt: Number(e.target.value || 10) } as any)}
                  />
                </div>
              </div>

              <div className='col-3'>
                <select
                  className='form-select form-select-sm'
                  value={String(f.align ?? 'center')}
                  onChange={e => setLayoutField(fieldKey as any, { align: e.target.value as Align } as any)}
                >
                  <option value='left'>Left</option>
                  <option value='center'>Center</option>
                  <option value='right'>Right</option>
                </select>
              </div>

              <div className='col-3'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={!!f.bold}
                    onChange={e => setLayoutField(fieldKey as any, { bold: e.target.checked } as any)}
                  />
                  <label className='form-check-label small'>Bold</label>
                </div>
              </div>
            </>
          )}

          {isBarcode && (
            <>
              <div className='col-4'>
                <select
                  className='form-select form-select-sm'
                  value={String(f.format ?? 'CODE128')}
                  onChange={e => setLayoutField(fieldKey as any, { format: e.target.value } as any)}
                >
                  <option value='CODE128'>CODE128</option>
                  <option value='EAN13'>EAN13</option>
                  <option value='CODE39'>CODE39</option>
                </select>
              </div>

              <div className='col-4'>
                <div className='input-group input-group-sm'>
                  <span className='input-group-text'>BarW(px)</span>
                  <input
                    type='number'
                    step='0.1'
                    className='form-control'
                    value={Number(f.barWidthPx ?? 1.4)}
                    onChange={e => setLayoutField(fieldKey as any, { barWidthPx: Number(e.target.value || 1.4) } as any)}
                  />
                </div>
              </div>

              <div className='col-4'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={!!f.showValue}
                    onChange={e => setLayoutField(fieldKey as any, { showValue: e.target.checked } as any)}
                  />
                  <label className='form-check-label small'>Show Text</label>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
